import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
    ArrowRight, Sparkles, Users, Rocket, Clock, ShieldCheck,
    Smartphone, Search, Zap, Layers, BarChart3, Cookie,
} from 'lucide-react';
import Hero3DGlobe from './Hero3DGlobe';
import OrbitSatellites from './OrbitSatellites';
import OrbitDetailPanel from './OrbitDetailPanel';

/**
 * Premium cinematic hero — split composition.
 *   Left column: pill badge, three-line headline, subtitle, two CTAs, trust dots.
 *   Right column: dominant 3D globe surrounded by light arcs + platform ripple.
 *   Bottom: 4-stat glass strip spanning the viewport.
 * Functionality, content anchors, scroll behaviour and testids preserved.
 */

const FADE_UP = {
    hidden: { opacity: 0, y: 30, filter: 'blur(10px)' },
    show: {
        opacity: 1,
        y: 0,
        filter: 'blur(0px)',
        transition: { duration: 0.95, ease: [0.16, 1, 0.3, 1] },
    },
};

const STAGGER = {
    show: { transition: { staggerChildren: 0.12, delayChildren: 0.2 } },
};

const HERO_SERVICES = [
    {
        icon: Smartphone,
        slug: 'responsive-design',
        title: 'Responsive Design',
        description:
            'Pixel-perfect on every device, from mobile to desktop. Your website will look stunning on phones, tablets, laptops, and large screens. We test across 20+ device sizes.',
        features: ['Fluid layouts', 'Touch-optimised', 'Retina-ready images', 'Cross-browser tested'],
        priceFrom: '£499',
        whatYouGet: [
            'Custom layouts for mobile, tablet, desktop, and large screens',
            'We test on 20+ real devices before launch',
            'Touch-friendly navigation and buttons',
            'Retina-ready images that look sharp on any screen',
        ],
    },
    {
        icon: Search,
        slug: 'seo-optimisation',
        title: 'SEO Optimisation',
        description:
            'Built for Google from day one. Meta tags, schema markup, fast load times, XML sitemaps, and local SEO setup to get your business found.',
        features: ['Meta tags & schema', 'Google Business setup', 'XML sitemaps', 'Page speed optimised'],
        priceFrom: '£499',
        whatYouGet: [
            'Full on-page SEO: meta titles, descriptions, headings structure',
            'Schema markup for rich Google search results',
            'XML sitemap generation and submission',
            'Google Business Profile setup and optimisation',
            'Monthly ranking reports',
        ],
    },
    {
        icon: Zap,
        slug: 'performance',
        title: 'Performance',
        description:
            'Sub-2-second load times. Optimised images, clean code, CDN delivery, lazy loading, and caching strategies for maximum speed.',
        features: ['CDN delivery', 'Image optimisation', 'Code minification', 'Caching setup'],
        priceFrom: '£499',
        whatYouGet: [
            'Sub-2-second load times guaranteed',
            'Image compression and lazy loading',
            'Code minification (HTML, CSS, JS)',
            'CDN setup for global delivery',
            'Browser caching configuration',
            'Monthly speed reports via Google PageSpeed',
        ],
    },
    {
        icon: Layers,
        slug: 'content-management',
        title: 'Content Management',
        description:
            'Easy-to-use CMS so you can update your own content anytime. We set up and train you on a system that makes editing simple.',
        features: ['WordPress/CMS setup', 'Staff training included', 'Easy media uploads', 'Blog-ready'],
        priceFrom: '£499',
        whatYouGet: [
            'WordPress or headless CMS setup',
            'Custom admin dashboard — easy to use, no coding needed',
            'Staff training session (1 hour, recorded)',
            'Media library for images and documents',
            'Blog setup with categories and tags',
            'User roles (admin, editor, viewer)',
        ],
    },
    {
        icon: BarChart3,
        slug: 'analytics-tracking',
        title: 'Analytics & Tracking',
        description:
            'Google Analytics 4, conversion tracking, and heatmaps setup. Know exactly where your visitors come from and what they do.',
        features: ['GA4 setup', 'Conversion tracking', 'Heatmaps', 'Monthly reports'],
        priceFrom: '£499',
        whatYouGet: [
            'Google Analytics 4 full setup',
            'Conversion tracking for forms, calls, purchases',
            'Heatmaps showing where visitors click',
            'Monthly analytics report emailed to you',
            'Goal tracking and funnel analysis',
            'GDPR-compliant cookie consent integration',
        ],
    },
    {
        icon: Cookie,
        slug: 'gdpr-compliance',
        title: 'GDPR Compliance',
        description:
            'Cookie consent, privacy policy, and data handling built in. We make sure your site is fully compliant with UK and EU data regulations.',
        features: ['Cookie consent banner', 'Privacy policy page', 'Data handling setup', 'ICO compliant'],
        priceFrom: '£499',
        whatYouGet: [
            'Cookie consent banner (customisable design)',
            'Privacy policy page (written for your business)',
            'Terms and conditions page',
            'Data processing documentation',
            'ICO registration guidance',
            'Regular compliance reviews',
        ],
    },
];

const Hero = () => {
    const [activeServiceIndex, setActiveServiceIndex] = useState(null);

    const scrollTo = (id) => (e) => {
        e.preventDefault();
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <section
            className="relative min-h-screen flex flex-col justify-between overflow-hidden pt-24 pb-6 md:pt-28 md:pb-10"
            data-testid="hero-section"
        >
            {/* ===== Cinematic deep-space background ===== */}
            <div
                className="absolute inset-0 z-0 pointer-events-none"
                aria-hidden="true"
                style={{
                    background:
                        'radial-gradient(ellipse 80% 60% at 50% 0%, #0B2240 0%, #061632 35%, #03101F 70%, #020815 100%)',
                }}
            />
            <motion.div
                className="absolute inset-0 z-0 pointer-events-none"
                aria-hidden="true"
                animate={{
                    background: [
                        'radial-gradient(55% 55% at 75% 38%, rgba(56,189,248,0.30) 0%, transparent 60%), radial-gradient(45% 40% at 20% 75%, rgba(99,102,241,0.20) 0%, transparent 60%)',
                        'radial-gradient(55% 55% at 78% 42%, rgba(56,189,248,0.34) 0%, transparent 60%), radial-gradient(45% 40% at 25% 70%, rgba(124,58,237,0.20) 0%, transparent 60%)',
                        'radial-gradient(55% 55% at 75% 38%, rgba(56,189,248,0.30) 0%, transparent 60%), radial-gradient(45% 40% at 20% 75%, rgba(99,102,241,0.20) 0%, transparent 60%)',
                    ],
                }}
                transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut' }}
            />
            {/* Soft vignette to deepen the edges */}
            <div
                className="absolute inset-0 z-0 pointer-events-none"
                aria-hidden="true"
                style={{
                    background:
                        'radial-gradient(ellipse 90% 70% at 50% 50%, transparent 50%, rgba(0,0,0,0.55) 100%)',
                }}
            />

            {/* Faint nebula clouds — volumetric depth behind everything */}
            <motion.div
                className="absolute inset-0 z-0 pointer-events-none"
                aria-hidden="true"
                style={{
                    background:
                        'radial-gradient(40% 30% at 72% 28%, rgba(125,211,252,0.20) 0%, transparent 70%),' +
                        'radial-gradient(35% 28% at 18% 70%, rgba(167,139,250,0.16) 0%, transparent 70%),' +
                        'radial-gradient(30% 22% at 86% 78%, rgba(56,189,248,0.14) 0%, transparent 70%)',
                    filter: 'blur(12px)',
                    mixBlendMode: 'screen',
                }}
                animate={{ opacity: [0.85, 1, 0.85] }}
                transition={{ duration: 11, repeat: Infinity, ease: 'easeInOut' }}
            />

            {/* ===== Main content grid ===== */}
            <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-8 flex-1 flex items-center">
                <div className="grid grid-cols-1 lg:grid-cols-12 items-center gap-y-12 gap-x-8 w-full">

                    {/* ============ LEFT: copy ============ */}
                    <motion.div
                        className="lg:col-span-6 text-center lg:text-left"
                        variants={STAGGER}
                        initial="hidden"
                        animate="show"
                    >
                        {/* Pill badge */}
                        <motion.div
                            variants={FADE_UP}
                            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/[0.08] backdrop-blur-md border border-white/[0.15] shadow-[0_4px_16px_rgba(56,189,248,0.18)]"
                            data-testid="hero-label"
                        >
                            <Sparkles className="w-3.5 h-3.5 text-[#7DD3FC]" />
                            <span className="font-mono text-[10.5px] tracking-[0.18em] text-white uppercase font-semibold">
                                #1 AI-Powered Web Agency · UK
                            </span>
                        </motion.div>

                        {/* Headline — "Automate. Optimize. Create success." */}
                        <motion.h1
                            variants={FADE_UP}
                            className="mt-7 font-display font-semibold leading-[0.98] tracking-[-0.025em] text-white text-balance text-[56px] sm:text-[68px] md:text-[76px] lg:text-[84px] xl:text-[92px]"
                            style={{
                                textShadow:
                                    '0 2px 24px rgba(56,189,248,0.35), 0 18px 48px rgba(0,0,0,0.55)',
                            }}
                            data-testid="hero-title"
                        >
                            Automate.
                            <br />
                            Optimize.
                            <br />
                            Create{' '}
                            <span className="relative inline-block">
                                <span
                                    className="bg-clip-text text-transparent"
                                    style={{
                                        backgroundImage:
                                            'linear-gradient(135deg, #7DD3FC 0%, #60A5FA 45%, #A78BFA 100%)',
                                    }}
                                >
                                    success
                                </span>
                                <motion.svg
                                    className="absolute -bottom-2 left-0 w-full"
                                    height="8"
                                    viewBox="0 0 200 8"
                                    fill="none"
                                    preserveAspectRatio="none"
                                    aria-hidden="true"
                                >
                                    <motion.path
                                        d="M2 5 Q 50 1, 100 4 T 198 3"
                                        stroke="#7DD3FC"
                                        strokeWidth="3"
                                        strokeLinecap="round"
                                        initial={{ pathLength: 0 }}
                                        animate={{ pathLength: 1 }}
                                        transition={{ duration: 1.1, delay: 1.0, ease: 'easeOut' }}
                                    />
                                </motion.svg>
                            </span>
                            <span className="text-white">.</span>
                        </motion.h1>

                        {/* Subtitle */}
                        <motion.p
                            variants={FADE_UP}
                            className="mt-7 text-[16px] sm:text-[17px] text-slate-300 max-w-xl mx-auto lg:mx-0 leading-relaxed"
                            data-testid="hero-subtitle"
                        >
                            <span className="font-semibold text-white">RevoloAIWEB</span>{' '}
                            provides you with all the AI tools you need to turn ideas into
                            real results and automate your entire business.
                        </motion.p>

                        {/* CTAs */}
                        <motion.div
                            variants={FADE_UP}
                            className="mt-9 flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-3.5"
                        >
                            <motion.a
                                href="#website-demo"
                                onClick={scrollTo('website-demo')}
                                whileHover={{ y: -2, scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                transition={{ type: 'spring', stiffness: 380, damping: 22 }}
                                className="inline-flex items-center gap-2 bg-gradient-to-br from-[#3B82F6] to-[#1D4ED8] text-white font-semibold text-[15px] px-7 py-3.5 rounded-full"
                                style={{
                                    boxShadow:
                                        '0 4px 15px rgba(0, 200, 255, 0.30), 0 8px 30px rgba(0, 200, 255, 0.15)',
                                    transition: 'box-shadow 0.4s ease, transform 0.4s ease',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.boxShadow =
                                        '0 6px 20px rgba(0, 200, 255, 0.40), 0 12px 40px rgba(0, 200, 255, 0.20)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.boxShadow =
                                        '0 4px 15px rgba(0, 200, 255, 0.30), 0 8px 30px rgba(0, 200, 255, 0.15)';
                                }}
                                data-testid="hero-cta-quote"
                            >
                                Get a Free Quote
                                <ArrowRight className="w-4 h-4" />
                            </motion.a>
                            <motion.a
                                href="#pricing"
                                onClick={scrollTo('pricing')}
                                whileHover={{ y: -2, scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                transition={{ type: 'spring', stiffness: 380, damping: 22 }}
                                className="inline-flex items-center gap-2 bg-white/[0.06] backdrop-blur-sm text-white border border-white/[0.18] font-semibold text-[15px] px-7 py-3.5 rounded-full shadow-[0_8px_24px_rgba(0,0,0,0.25)] hover:bg-white/[0.10] transition-colors"
                                data-testid="hero-cta-plans"
                            >
                                View Our Plans
                            </motion.a>
                        </motion.div>

                        {/* Trust row */}
                        <motion.div
                            variants={FADE_UP}
                            className="mt-8 flex flex-wrap items-center justify-center lg:justify-start gap-x-6 gap-y-2 text-[13px] text-slate-300"
                            data-testid="hero-trust-chip"
                        >
                            <div className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-[#60A5FA] shadow-[0_0_8px_rgba(96,165,250,0.8)]" />
                                <span>50+ UK Businesses</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-[#34D399] shadow-[0_0_8px_rgba(52,211,153,0.8)]" />
                                <span>Launch in 14 Days</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-[#A78BFA] shadow-[0_0_8px_rgba(167,139,250,0.8)]" />
                                <span>AI Security Included</span>
                            </div>
                        </motion.div>
                    </motion.div>

                    {/* ============ RIGHT: dominant globe ============ */}
                    <div className="lg:col-span-6 relative">
                        <GlobeStage />
                        {/* Satellite service cards orbiting around the globe (desktop only).
                            Visually overlaps the globe via absolute positioning. Mouse events
                            pass through the wrapper so dragging the globe still works. */}
                        <OrbitSatellites
                            services={HERO_SERVICES}
                            activeIndex={activeServiceIndex}
                            onSelect={setActiveServiceIndex}
                        />
                    </div>
                </div>
            </div>

            {/* ===== Service detail panel — overlays the hero when a satellite is clicked ===== */}
            <OrbitDetailPanel
                services={HERO_SERVICES}
                activeIndex={activeServiceIndex}
                onClose={() => setActiveServiceIndex(null)}
            />

            {/* ===== Bottom stats strip ===== */}
            <motion.div
                className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-8 mt-8 md:mt-12"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 1.1, ease: [0.16, 1, 0.3, 1] }}
            >
                <div
                    className="rounded-[28px] bg-white/[0.06] backdrop-blur-xl border border-white/[0.12] shadow-[0_18px_48px_rgba(56,189,248,0.18),inset_0_1px_0_rgba(255,255,255,0.06)] px-5 sm:px-8 py-5 sm:py-6"
                    data-testid="hero-stats"
                >
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-y-5 gap-x-4">
                        {[
                            { icon: Users,       value: '500+', label: 'Active Clients',    bg: 'rgba(59,130,246,0.16)',  color: '#7DD3FC' },
                            { icon: Rocket,      value: '98%',  label: 'Success Rate',      bg: 'rgba(167,139,250,0.18)', color: '#A78BFA' },
                            { icon: Clock,       value: '10x',  label: 'More Productive',   bg: 'rgba(56,189,248,0.18)',  color: '#7DD3FC' },
                            { icon: ShieldCheck, value: '24/7', label: 'Dedicated Support', bg: 'rgba(96,165,250,0.18)',  color: '#60A5FA' },
                        ].map((s, i) => (
                            <div key={i} className="flex items-center gap-3.5">
                                <span
                                    className="inline-flex items-center justify-center w-12 h-12 rounded-2xl shrink-0"
                                    style={{ backgroundColor: s.bg, boxShadow: `0 0 18px ${s.color}40` }}
                                >
                                    <s.icon className="w-5 h-5" style={{ color: s.color }} />
                                </span>
                                <div className="leading-tight">
                                    <div className="font-display text-[26px] sm:text-[28px] font-semibold text-white tracking-tight">
                                        {s.value}
                                    </div>
                                    <div className="text-[12.5px] text-slate-400">{s.label}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </motion.div>

            {/* Smooth dark→light transition at the very bottom of the hero, so the
                cinematic space dissolves into the light sections without a hard seam. */}
            <div
                className="absolute inset-x-0 bottom-0 h-32 z-[2] pointer-events-none"
                aria-hidden="true"
                style={{
                    background:
                        'linear-gradient(to bottom, rgba(2,8,21,0) 0%, rgba(2,8,21,0.4) 30%, rgba(214,231,255,0.85) 80%, rgba(238,245,255,1) 100%)',
                }}
            />

            {/* Scroll cue (desktop only, centered under stage) */}
            <motion.div
                className="hidden md:flex absolute left-1/2 z-10 -translate-x-1/2 pointer-events-none flex-col items-center gap-1.5"
                style={{ top: 'calc(100vh - 200px)' }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.8, duration: 0.8 }}
                aria-hidden="true"
            >
                <motion.div
                    className="flex flex-col items-center gap-1.5"
                    animate={{ y: [0, 6, 0] }}
                    transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
                >
                    <span className="text-[10px] font-mono tracking-[0.3em] text-[#7DD3FC] uppercase font-semibold">
                        Scroll
                    </span>
                    <div className="w-[1.5px] h-6 bg-gradient-to-b from-[#7DD3FC]/80 to-transparent" />
                </motion.div>
            </motion.div>
        </section>
    );
};

/* ============================================================
   GlobeStage — cinematic right-side composition
   ============================================================ */
const GlobeStage = () => (
    <motion.div
        className="relative w-full mx-auto"
        style={{ aspectRatio: '1 / 1', maxWidth: '700px' }}
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
    >
        {/* Soft blue aura halo */}
        <motion.div
            className="absolute inset-[-8%] rounded-full pointer-events-none"
            style={{
                background:
                    'radial-gradient(closest-side, rgba(56,189,248,0.65) 0%, rgba(37,99,235,0.32) 30%, rgba(99,102,241,0.16) 50%, rgba(255,255,255,0) 75%)',
                filter: 'blur(48px)',
            }}
            animate={{ opacity: [0.75, 1.0, 0.75], scale: [1, 1.05, 1] }}
            transition={{ duration: 6.5, repeat: Infinity, ease: 'easeInOut' }}
            aria-hidden="true"
        />

        {/* Globe */}
        <div className="absolute inset-[7%] z-[2]">
            <Hero3DGlobe />
        </div>

        {/* Light arc orbits */}
        <LightArcs />

        {/* Platform ripple */}
        <PlatformRipple />
    </motion.div>
);

const LightArcs = () => {
    const arcs = [
        { rotate: -18, scaleY: 0.22, dur: 22, opacity: 0.75 },
        { rotate: 8,   scaleY: 0.30, dur: 28, opacity: 0.60 },
        { rotate: -32, scaleY: 0.16, dur: 34, opacity: 0.50 },
    ];
    return (
        <div className="absolute inset-0 pointer-events-none z-[3]" aria-hidden="true">
            {arcs.map((a, i) => (
                <motion.div
                    key={i}
                    className="absolute left-1/2 top-1/2 rounded-full"
                    style={{
                        width: '128%',
                        height: '128%',
                        translateX: '-50%',
                        translateY: '-50%',
                        border: '1.5px solid rgba(255,255,255,0.92)',
                        boxShadow: '0 0 18px rgba(125,211,252,0.65), inset 0 0 18px rgba(125,211,252,0.30)',
                        transform: `translate(-50%, -50%) rotate(${a.rotate}deg) scaleY(${a.scaleY})`,
                        opacity: a.opacity,
                    }}
                    animate={{ rotate: [a.rotate, a.rotate + 360] }}
                    transition={{ duration: a.dur, repeat: Infinity, ease: 'linear' }}
                />
            ))}
            <motion.div
                className="absolute left-1/2 top-1/2 rounded-full"
                style={{
                    width: '140%',
                    height: '140%',
                    translateX: '-50%',
                    translateY: '-50%',
                    background:
                        'conic-gradient(from 0deg, rgba(125,211,252,0) 0deg, rgba(125,211,252,0.70) 18deg, rgba(255,255,255,0.98) 30deg, rgba(125,211,252,0.70) 42deg, rgba(125,211,252,0) 60deg, rgba(125,211,252,0) 360deg)',
                    maskImage: 'radial-gradient(closest-side, transparent 64%, black 66%, black 71%, transparent 73%)',
                    WebkitMaskImage: 'radial-gradient(closest-side, transparent 64%, black 66%, black 71%, transparent 73%)',
                    filter: 'blur(0.5px)',
                }}
                animate={{ rotate: 360 }}
                transition={{ duration: 14, repeat: Infinity, ease: 'linear' }}
            />
        </div>
    );
};

const PlatformRipple = () => {
    const rings = [
        { size: 70,  opacity: 0.85, dur: 5.0, delay: 0 },
        { size: 84,  opacity: 0.70, dur: 5.6, delay: 0.3 },
        { size: 98,  opacity: 0.55, dur: 6.2, delay: 0.6 },
        { size: 112, opacity: 0.42, dur: 6.8, delay: 0.9 },
        { size: 126, opacity: 0.30, dur: 7.4, delay: 1.2 },
        { size: 140, opacity: 0.20, dur: 8.0, delay: 1.5 },
    ];
    return (
        <div
            className="absolute left-1/2 -translate-x-1/2 pointer-events-none z-[1]"
            style={{ bottom: '-8%', width: '140%', height: '42%' }}
            aria-hidden="true"
        >
            {/* Bright base disc */}
            <div
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
                style={{
                    width: '95%',
                    height: '48%',
                    background:
                        'radial-gradient(closest-side, rgba(125,211,252,0.85) 0%, rgba(56,189,248,0.45) 30%, rgba(99,102,241,0.18) 55%, rgba(255,255,255,0) 78%)',
                    filter: 'blur(18px)',
                }}
            />
            {rings.map((r, i) => (
                <motion.div
                    key={i}
                    className="absolute left-1/2 top-1/2 rounded-full"
                    style={{
                        width: `${r.size}%`,
                        height: `${r.size * 0.34}%`,
                        translateX: '-50%',
                        translateY: '-50%',
                        border: '1.5px solid rgba(125,211,252,0.95)',
                        boxShadow:
                            '0 0 18px rgba(125,211,252,0.75), inset 0 0 12px rgba(125,211,252,0.35)',
                    }}
                    animate={{
                        opacity: [r.opacity * 0.5, r.opacity, r.opacity * 0.5],
                        scale: [0.96, 1.04, 0.96],
                    }}
                    transition={{
                        duration: r.dur,
                        repeat: Infinity,
                        ease: 'easeInOut',
                        delay: r.delay,
                    }}
                />
            ))}
            {/* Bottom shadow ellipse for grounding */}
            <div
                className="absolute left-1/2 -translate-x-1/2 rounded-full"
                style={{
                    bottom: '-2%',
                    width: '60%',
                    height: '14%',
                    background:
                        'radial-gradient(closest-side, rgba(0,0,0,0.55) 0%, transparent 70%)',
                    filter: 'blur(8px)',
                }}
            />
        </div>
    );
};

export default Hero;
